import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {DirectoryService} from '../../services/directory.service';
import {of} from 'rxjs/observable/of';
import {catchError, mergeMap, tap} from 'rxjs/operators';
import {BuildingApiService} from '../../services/api/building-api.service';

import {
    AddressChanges,
    DateChanges,
    EmailChanges,
    EmergencyContactChanges,
    GroupsChanges,
    IChanges,
    NameChanges,
    PetChanges,
    PhoneChanges,
    PhotoChanges,
    Solution,
    TenantChanges,
    VehicleChanges
} from './user-updates-modal.types';
import {IResidentPatch, PatchAction} from '../../types/api-response/building/resident-patch';
import {ISuite} from '../../types/api-response/building/suite';
import {UserPhoneTypeTitle} from '../../types/api-response/building/user-phone';
import {ISuiteUser, ResidentRole} from '../../types/api-response/building/suite-user';
import {IPet} from '../../types/api-response/building/pet';
import {IUser, User} from '../../types/api-response/building/user';
import {IVehicle} from '../../types/api-response/building/vehicle';
import {IBuilding} from '../../types/api-response/building';
import {Observable} from 'rxjs/Observable';
import {concat} from 'rxjs/observable/concat';
import {forkJoin} from 'rxjs/observable/forkJoin';
import {ApiError} from '../../types/api-response/errors';
import {IToastr} from '../../types/toastr';
import {NgForm} from '@angular/forms';

declare const toastr: IToastr;
declare const moment: any;

@Component({
    selector: 'user-updates-modal',
    templateUrl: './user-updates-modal.component.html',
    styleUrls: ['./user-updates-modal.component.scss']
})
export class UserUpdatesModalComponent implements OnInit, AfterViewInit, OnDestroy {

    deleteUser: boolean;

    public autoApprove = false;

    private changesLoaded$: EventEmitter<IChanges[]> = new EventEmitter();
    /**
     * Rises on resident patch process completed
     */
    @Output() patched: EventEmitter<IResidentPatch> = new EventEmitter();

    /**
     * Triggers patch details data loading
     * @param patch
     */
    @Input('patch') set currentPatch(patch: IResidentPatch) {
        this.loadPatchDetails(patch);
    }

    /**
     * Change items
     */
    changes: IChanges[];

    /**
     * ResidentPatch data
     */
    patch: IResidentPatch & {suiteUser: ISuiteUser};

    /**
     * All patches available for the current user
     */
    allPatches: IResidentPatch[] = [];

    /**
     * Current patch users suite data
     */
    suite: ISuite;

    /**
     * Enumerable solution options
     */
    solution = Solution;

    /**
     * Pet type details
     */
    petTypes = [];

    /**
     * User Groups list
     */
    userGroups = [];

    /**
     * Row output template references
     */
    @ViewChild('nameTpl') nameTpl: TemplateRef<any>;
    @ViewChild('emailTpl') emailTpl: TemplateRef<any>;
    @ViewChild('photoTpl') photoTpl: TemplateRef<any>;
    @ViewChild('groupsTpl') groupsTpl: TemplateRef<any>;
    @ViewChild('addressTpl') addressTpl: TemplateRef<any>;
    @ViewChild('phoneTpl') phoneTpl: TemplateRef<any>;
    @ViewChild('dateTpl') dateTpl: TemplateRef<any>;
    @ViewChild('emergencyContactTpl') emergencyContactTpl: TemplateRef<any>;
    @ViewChild('vehicleTpl') vehicleTpl: TemplateRef<any>;
    @ViewChild('petTpl') petTpl: TemplateRef<any>;
    @ViewChild('tenantTpl') tenantTpl: TemplateRef<any>;

    @ViewChild('form') form: NgForm;

    /**
     * Template references collection
     */
    rowTpl: { [key: string]: TemplateRef<any> };

    /**
     * User phone display titles
     */
    userPhoneTitles = UserPhoneTypeTitle;

    /**
     * Denotes an API query process
     */
    isDataProcessing = false;

    isPatchDetailsLoading = false;

    constructor(
        private buildingApi: BuildingApiService,
        private directoryService: DirectoryService,
    ) {
    }

    ngOnInit() {
        this.buildingApi.getPetTypes().subscribe((res) => {
            this.petTypes = res.entities;
        });

        this.buildingApi.getAllUserGroups().subscribe((res) => {
            this.userGroups = res.entities;
        });

        this.buildingApi.getAllResidentPatches('suite.building,suiteUser.user.groups').subscribe(
            result => this.allPatches = result.entities
        );

        this.changesLoaded$.subscribe((changes: IChanges[]) => {
            this.autoPatch();
        });
    }

    /**
     * Loads patch details
     *
     * @param patch
     */
    private loadPatchDetails(patch: IResidentPatch | string) {
        this.isPatchDetailsLoading = true;
        this.deleteUser = null;

        const patch$ = typeof patch === 'string' ? this.buildingApi.getResidentPatch(patch, 'suite.building,suiteUser.user.groups') : of(patch);

        patch$.pipe(
            tap(patch => this.patch = patch as IResidentPatch & {suiteUser: ISuiteUser}),
            mergeMap(patch => forkJoin(
                [
                    of(patch),
                    this.buildingApi.getSuite((patch.suite as ISuite).id, 'building,suiteUser.user,pets,vehicles')
                ]))
        ).subscribe(([, suite]) => {
            this.suite = suite as ISuite;

            this.setUpdates();
            this.isPatchDetailsLoading = false;
        });
    }

    /**
     * Sets resident patch changes
     */
    private setUpdates() {
        const suiteUser = (this.patch.suiteUser || {} as ISuiteUser);
        const user = suiteUser.user || new User();

        const patchFullName = this.patch.userProfile && this.patch.userProfile.fullName;
        const patchGroups = (this.patch.userProfile && this.patch.userProfile.groups) || null;
        const patchEmail = (this.patch.userProfile && this.patch.userProfile.email) || null;
        const patchPhoto = this.patch.userProfile && this.patch.userProfile.photo;
        const patchDateOfBirth = this.patch.userProfile && this.patch.userProfile.dateOfBirth;
        const patchEmergencyContacts = this.patch.userProfile && this.patch.userProfile.emergencyContacts;
        const suiteTenants = this.suite.suiteUser.filter(suiteUser => suiteUser.role == ResidentRole.tenant).map(suiteUser => suiteUser.user); // filter tenants
        const ownVehicles = this.suite.vehicles.filter(vehicle => vehicle.owner == user.id); // filter own vehicles

        this.changes = [
            new NameChanges('Name', user.fullName, patchFullName),
            new EmailChanges('Email', user.email, patchEmail),
            new PhotoChanges('Photo', user.photo, patchPhoto),
            new GroupsChanges('Groups', user.groups, patchGroups),
            new AddressChanges('Address', user.addresses, this.patch.addresses),
            new PhoneChanges('Phones', user.phones, this.patch.phones),
           // new DateChanges('Date of Birth', user.dateOfBirth, patchDateOfBirth),
            new EmergencyContactChanges('Emergency Contacts', user.emergencyContacts, patchEmergencyContacts),
            new VehicleChanges('Vehicles', ownVehicles, this.patch.vehicles ),
            new PetChanges('Pets', this.suite.pet, this.patch.pets),
            new TenantChanges('Owner w/tenant', suiteTenants, this.isOwner() ? this.patch.tenants : null),
        ];
        this.form.resetForm();
        this.form.reset();

        this.changesLoaded$.emit(this.changes);
    }

    /**
     * Is current user suite owner
     */
    private isOwner(): boolean {
        const suiteUser = this.patch.suiteUser;
        return [
            ResidentRole.owner_off_site,
            ResidentRole.owner_on_site
        ].includes(suiteUser ? suiteUser.role : null);
    }

    /**
     * Sets template collection
     * after view initialized
     */
    ngAfterViewInit() {
        this.rowTpl = {
            name: this.nameTpl,
            email: this.emailTpl,
            photo: this.photoTpl,
            groups: this.groupsTpl,
            address: this.addressTpl,
            phone: this.phoneTpl,
            date: this.dateTpl,
            emergencyContact: this.emergencyContactTpl,
            vehicle: this.vehicleTpl,
            pet: this.petTpl,
            tenant: this.tenantTpl,
        };
    }

    /**
     * Defines whether to show spinner
     */
    get isShowSpinner() {
        return !this.changes || this.isDataProcessing || this.isPatchDetailsLoading;
    }

    get patchIndex() {
        const patch = this.allPatches.find(patch => patch.id === this.patch.id);
        return patch ? this.allPatches.indexOf(patch) : 0;
    }

    /**
     * Closes this modal
     */
    closeModal() {
        this.directoryService.userUpdateModalData = null;
    }

    /**
     * Skips current resident patch
     */
    goToNext(unsetCurrent = false) {
        const index = this.patchIndex;
        let nextIndex = index + 1;

        if(unsetCurrent) {
            this.allPatches.splice(index, 1);
            nextIndex = index;
        }

        this.directoryService.userUpdateModalData = this.allPatches.length > nextIndex ? this.allPatches[nextIndex] : this.allPatches[0];
    }

    /**
     * Apply solution for all changes
     *
     * @param solution
     */
    public checkAll(solution: Solution) {
        this.changes
            .filter(row => row.patched && row.changed && !row.ignore) // only patched and changed but not ignored rows
            .forEach(row => row.solution = solution);
    }

    /**
     * Return is delete/Move out resident
     */
    public isDeleteAction(): boolean {
        return this.patch.action === PatchAction.ACTION_DELETE;
    }

    /**
     * Returns approved resident patch changes
     */
    public getApprovedDetails() {
        return this.changes.filter(row => row.solution === Solution.approve && !row.ignore);
    }

    /**
     * Returns phone type title
     * for selected type
     *
     * @param type
     */
    public getPhoneTypeTitle(type): string {
        return this.userPhoneTitles[type];
    }

    /**
     * Returns pet type title
     * for selected type ID
     *
     * @param typeId
     */
    public getPetTypeTitle(typeId): string {
        const typeEl = this.petTypes.find(el => +el.id === +typeId);
        return typeEl ? typeEl.name : '';
    }

    /**
     * Returns group title
     * for selected group ID
     *
     * @param groupId
     */
    public getGroupTitle(groupId): string {
        const typeEl = this.userGroups.find(el => +el.id === +groupId);
        return typeEl ? typeEl.name : '';
    }

    /**
     * On form submit event
     *
     * @param form
     */
    public onSubmit(form) {
        if (form.invalid) {

            return;
        }
        this.patchResident();
    }

    private patchResident()
    {
        this.isDataProcessing = true;

        const buildingID = (this.suite.building as IBuilding).id;
        const userProfile: Partial<IUser> = {};
        let pets: IPet[] = [];
        let vehicles: IVehicle[] = [];
        let tenants: IUser[] = [];

        // List of observable for api requests
        const observables$: Observable<any>[] = [];

        let suiteUser$ = of(this.patch.suiteUser);

        if (this.isDeleteAction()) {
            suiteUser$.subscribe((suiteUser: ISuiteUser) => {
                if (this.deleteUser) {
                    const moveOutDate = moment(this.patch.createdAt).format('YYYY-MM-DD');
                    observables$.push(this.buildingApi.updateSuiteUser({
                        id: suiteUser.id,
                        moveOutDate: moveOutDate
                    }));
                }
                this.processAllResponses(observables$);
            });
            return;
        }

        if (!this.patch.suiteUser) {
            const role = (this.patch.userProfile as any).role;
            suiteUser$ = this.buildingApi.createSuiteUser({
                building: buildingID,
                suite: this.suite.id,
                role: role == 'tenant' ? 'rent' : role
            }).pipe(
                mergeMap((res: ISuiteUser) => this.buildingApi.getSuiteUser(res.id, 'suite,user,building'))
            );
        }

        suiteUser$.subscribe((suiteUser: ISuiteUser) => {
            this.patch.suiteUser = suiteUser;


            this.getApprovedDetails().forEach(row => {
                if(row instanceof PhotoChanges) {
                    userProfile.photo = row.getPatch();
                }

                if(row instanceof NameChanges) {
                    userProfile.fullName = row.getPatch();
                }

                if(row instanceof EmailChanges) {
                    userProfile.email = row.getPatch();
                }

                if(row instanceof DateChanges) {
                    userProfile.dateOfBirth = row.getPatch();
                }

                if(row instanceof EmergencyContactChanges) {
                    userProfile.emergencyContacts = row.getPatch();
                }

                if(row instanceof AddressChanges) {
                    userProfile.addresses = row.getPatch();
                }

                if(row instanceof GroupsChanges) {
                    userProfile.groups = row.getPatch();
                }

                if(row instanceof PhoneChanges) {
                    userProfile.phones = row.getPatch();
                }

                if(row instanceof PetChanges) {
                    pets = row.getPatch(this.suite);
                }

                if(row instanceof VehicleChanges) {
                    vehicles = row.getPatch(this.suite);
                }

                if(row instanceof TenantChanges) {
                    tenants = row.getPatch(this.suite);
                }
            });

            // Update user profile details

            if (Object.keys(userProfile).length) {
                observables$.push(this.buildingApi.updateSuiteUser({
                    id: suiteUser.id,
                    user: userProfile as IUser
                }));
            }

            // update suite pets
            pets.forEach(pet => {
                observables$.push(this.buildingApi.setPet(pet));
            });

            // update suite vehicles
            vehicles.forEach(vehicle => {
                observables$.push(this.buildingApi.checkAndSetVehicle(vehicle));
            });

            // set user tenants (rent)
            tenants.forEach(tenant => {
                observables$.push(this.buildingApi.setSuiteUser({
                    building: buildingID,
                    suite: this.suite.id,
                    role: ResidentRole.tenant,
                    user: tenant
                }).pipe(
                    catchError((res) => {
                        if (res.errors && res.errors[0].error === ApiError.SuiteEmailDuplicate) {
                            toastr.error('Email address ' + tenant.email + ' belongs to another resident in this unit.');
                        }

                        return of(null);
                    })
                ));
            });

             this.processAllResponses(observables$);
        });
    }

    /**
     * @param observables$
     */
    private processAllResponses(observables$: Observable<any>[] = []) {

        observables$.push(this.buildingApi.processResidentPatch(this.patch.id));

        let hasError = false;

        // process all responses
        concat(...observables$).subscribe(
            (res) => {
                console.log('Success', res);
            },
            (error) => {
                console.log('ERROR', error);
                hasError = true;
                this.isDataProcessing = false;
            },
            () => {
                if(hasError) {
                    toastr.error('Failed to apply update');
                } else {
                    toastr.success('Updates applied successfully');
                    this.patched.emit(this.patch);
                    this.goToNext(true);
                }


                this.isDataProcessing = false;
            });
    }

    private autoPatch()
    {
        if (this.autoApprove) {
            this.changes.forEach(res => res.solution = Solution.approve);
            this.patchResident();
        }
    }

    public approveAll()
    {
        this.autoApprove = !this.autoApprove;
        this.autoPatch();
    }

    ngOnDestroy(): void {
        this.changesLoaded$.unsubscribe();
    }
}



// WEBPACK FOOTER //
// /app/src/app/components/user-updates-modal/user-updates-modal.component.ts